import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Star, Share2, Copy, Check, Zap, TrendingUp, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import apiClient from '@/api';
import Navbar from '@/components/Navbar';

const AVATAR_EMOJIS = ['🌱', '🌿', '🪴', '🌸', '🌺', '🌻', '✨', '🌟'];
const BADGE_EMOJIS = {
  'first_workout': '🏃', 'workout_warrior': '💪', 'fitness_master': '🏆',
  'goal_crusher': '🎯', 'meal_planner': '🍽️', 'social_butterfly': '🦋',
  'referral_champion': '🌟', 'consistency_king': '👑', 'team_player': '🤝'
};

export default function ProgressPage() {
  const [progress, setProgress] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [allBadges, setAllBadges] = useState([]);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [progRes, refRes, badgeRes] = await Promise.all([
          apiClient.get('/my/progress'),
          apiClient.get('/referrals/my'),
          apiClient.get('/badges')
        ]);
        setProgress(progRes.data);
        setReferrals(refRes.data);
        setAllBadges(badgeRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const copyReferral = () => {
    navigator.clipboard.writeText(referrals?.referral_code || '');
    setCopied(true);
    toast.success('System Code Copied');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F5F3EF] flex items-center justify-center font-black text-[#D4A574]">
      SYNCING PROGRESS...
    </div>
  );

  const xpProgress = progress?.next_level_info
    ? ((progress.xp - progress.current_level_info.xp_required) /
       (progress.next_level_info.xp_required - progress.current_level_info.xp_required)) * 100
    : 100;

  return (
    <div className="min-h-screen bg-[#F5F3EF] text-[#5C4A42] p-4 md:p-8">
      <Navbar />
      <div className="max-w-7xl mx-auto space-y-6">

        <header>
          <h1 className="text-4xl md:text-6xl font-black uppercase italic tracking-tighter">
            System <span className="text-[#D4A574]">Evolution</span>
          </h1>
          <p className="text-[#8B7355] font-mono text-[10px] uppercase tracking-[0.2em] mt-2">
            // Tracking growth from within
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* TILE 1: AVATAR STAGE (1x1) */}
          <motion.div className="warm-card p-8 rounded-3xl text-center flex flex-col items-center justify-center">
            <div className="text-8xl mb-6 drop-shadow-[0_0_25px_rgba(212,165,116,0.3)]">
              {AVATAR_EMOJIS[progress?.avatar_stage - 1] || '🌱'}
            </div>
            <h2 className="text-2xl font-black uppercase italic">LV {progress?.level}</h2>
            <p className="text-[#D4A574] font-bold text-xs tracking-widest uppercase">
              {progress?.current_level_info?.name}
            </p>
            <Link to="/levels" className="text-[10px] text-[#8B7355] underline uppercase tracking-widest mt-3 hover:text-[#D4A574]">
              View all levels
            </Link>
          </motion.div>

          {/* TILE 2: XP TELEMETRY (2x1) */}
          <motion.div className="md:col-span-2 warm-card p-8 rounded-3xl flex flex-col justify-between">
            <div className="flex justify-between items-end mb-4">
              <div>
                <span className="text-[10px] font-bold text-[#8B7355] uppercase tracking-widest">Experience Points</span>
                <div className="text-4xl font-black italic">{progress?.xp} <span className="text-sm text-[#A89284]">XP</span></div>
              </div>
              <div className="text-right">
                <span className="text-[10px] font-bold text-[#D4A574] uppercase tracking-widest">Next Phase</span>
                <div className="text-xl font-black italic">{progress?.next_level_info?.xp_required}</div>
              </div>
            </div>

            <div className="space-y-2">
              <Progress value={xpProgress} className="h-4 bg-[#FAF9F7] border border-[#D4C4B0]" indicatorClassName="bg-[#D4A574]" />
              <p className="text-[10px] font-mono text-[#8B7355] italic text-center">
                {progress?.xp_to_next_level} XP remaining until evolution
              </p>
            </div>

            <div className="mt-6 p-4 bg-[#D4A574]/10 border border-[#D4A574]/30 rounded-xl flex items-center justify-between">
              <span className="text-xs font-black uppercase italic">Phase Completion Reward:</span>
              <span className="text-[#D4A574] font-black text-xl">+{progress?.next_level_info?.points_reward || 0} PTS</span>
            </div>
          </motion.div>

          {/* TILE 3: ARMORY / BADGES (3x1) */}
          <div className="md:col-span-3 warm-card p-8 rounded-3xl">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-xl font-black uppercase italic flex items-center gap-3">
                <Trophy className="text-[#D4A574]" /> Achievement Armory
              </h3>
              <span className="font-mono text-xs text-[#8B7355]">{progress?.total_badges} / {progress?.available_badges} Unlocked</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {allBadges.map((badge) => {
                const earned = progress?.badges?.some(b => b.id === badge.id);
                return (
                  <div key={badge.id} className={`p-4 rounded-2xl border transition-all ${
                    earned ? 'bg-[#D4A574]/10 border-[#D4A574]/30' : 'bg-[#FAF9F7] border-[#D4C4B0] opacity-40'
                  }`}>
                    <div className="text-3xl mb-2">{BADGE_EMOJIS[badge.id] || badge.icon}</div>
                    <p className="text-[10px] font-black uppercase leading-tight">{badge.name}</p>
                    {earned && <div className="mt-2 text-[8px] font-mono text-[#D4A574]">COMPLETED</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* TILE 4: RECRUITMENT SYSTEM (3x1) */}
          <div className="md:col-span-3 bg-gradient-to-br from-white to-[#FAF9F7] border border-[#D4C4B0] p-8 rounded-3xl">
            <div className="grid md:grid-cols-2 gap-12">
              <div className="space-y-6">
                <h3 className="text-xl font-black uppercase italic flex items-center gap-3">
                  <Star className="text-[#D4A574]" /> Recruitment Link
                </h3>
                <div className="flex gap-2">
                  <div className="flex-1 bg-[#FAF9F7] border border-[#D4C4B0] rounded-xl px-4 py-4 text-2xl font-black text-[#D4A574] tracking-widest text-center uppercase">
                    {referrals?.referral_code || '---'}
                  </div>
                  <Button onClick={copyReferral} className="bg-[#D4A574] text-white hover:bg-[#C19563] h-full px-6">
                    {copied ? <Check /> : <Copy />}
                  </Button>
                </div>
                <div className="bg-[#8B7355]/10 border border-[#8B7355]/30 p-4 rounded-xl">
                  <p className="text-xs font-bold text-[#8B7355] uppercase mb-2">Commission Structure:</p>
                  <p className="text-sm font-black italic">Earn 200 Points per Successful Onboarding</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                 {[
                   { label: 'Recruits', val: referrals?.total_referrals },
                   { label: 'Active', val: referrals?.onboarded_referrals },
                   { label: 'Points', val: (referrals?.onboarded_referrals || 0) * 200, highlight: true }
                 ].map((stat, i) => (
                   <div key={i} className={`p-4 rounded-2xl border ${stat.highlight ? 'bg-[#D4A574] border-[#D4A574]' : 'bg-[#FAF9F7] border-[#D4C4B0]'}`}>
                     <div className={`text-3xl font-black italic ${stat.highlight ? 'text-white' : 'text-[#5C4A42]'}`}>{stat.val || 0}</div>
                     <p className={`text-[10px] font-bold uppercase ${stat.highlight ? 'text-white/70' : 'text-[#A89284]'}`}>{stat.label}</p>
                   </div>
                 ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
